
import { db } from './firebase';
import { doc, getDoc, setDoc, deleteDoc, deleteField, collection, getDocs, writeBatch, query, where } from 'firebase/firestore';
import type { IconType, IconCategory, CustomBackground, BackgroundCategory, CustomShape, CustomTemplate, VariantSettings } from '../types';

const SETTINGS_COLLECTION = 'settings';

const COL_ICONS = 'custom_icons';
const COL_BGS = 'custom_backgrounds';
const COL_SHAPES = 'custom_shapes';
const COL_TEMPLATES = 'custom_templates';

/**
 * Returns the Firestore document ID for this deployment instance.
 * Set via window.__INSTANCE_ID__ injected by server.js from the INSTANCE_ID env var.
 * Falls back to 'general' for local dev and legacy deployments.
 */
export function getInstanceId(): string {
    if (typeof window !== 'undefined' && (window as any).__INSTANCE_ID__) {
        return (window as any).__INSTANCE_ID__;
    }
    return 'general';
}

/** Základní instance služby (hlavní adresa bez cesty). Na adrese tématu se liší od getInstanceId(). */
export function getBaseInstanceId(): string {
    if (typeof window !== 'undefined' && (window as any).__BASE_INSTANCE_ID__) {
        return (window as any).__BASE_INSTANCE_ID__;
    }
    return getInstanceId();
}

export interface SiteTheme { slug: string; name: string }

/** Téma webu, na jehož adrese zákazník je (/pejsci …), nebo null na hlavní adrese. Vkládá server.js. */
export function getSiteTheme(): SiteTheme | null {
    if (typeof window === 'undefined') return null;
    const t = (window as any).__THEME__;
    return t && typeof t.slug === 'string' ? t : null;
}

export interface UserPermissions {
  allowAiGenerator: boolean;
  allowIconUpload: boolean;
  allowGlobalColors: boolean;
  allowFontSelection: boolean;
  allowStickerDimensions: boolean;
  allowStickerCount: boolean;
  allowTextStyling: boolean;
  allowFineTuning: boolean;
  allowFullImageUpload: boolean;
  allowSheetPositioning: boolean;
  allowStickerPositioning: boolean;
  allowNameField: boolean;
  allowClassNameField: boolean;
  allowSharpCorners: boolean;
  showGlobalNameField: boolean;
  showGlobalClassNameField: boolean;
  allowQuickSettings: boolean;
  showGlobalSettings: boolean;
  allowAddSticker: boolean;
  allowBrandFromWeb?: boolean; // Logo a barvy z webu firmy (výchozí vypnuto)
  showUsagePhotos?: boolean; // Ukázky použití: samolepky zákazníka vložené do fotek předmětů (výchozí zapnuto)
}

interface GeneralSettings {
  appTitle?: string;
  appSubtitle?: string;
  appLogo?: string;
  adminReturnUrl?: string;
  makeWebhookUrl?: string;
  errorWebhookUrl?: string;
  driveFolderId?: string;
  showBackgroundsToPublic?: boolean;
  allowUserUploads?: boolean;
  iconCategories?: IconCategory[];
  bgCategories?: BackgroundCategory[];
  nameLabel?: string;
  classNameLabel?: string;
  iconLabel?: string;
  addStickerButtonLabel?: string;
  sharedDesignLabel?: string;
  selectIconButtonLabel?: string;
  defaultNameValue?: string;
  defaultClassNameValue?: string;
  uploadButtonLabel?: string;
  wizardKicker?: string; // Quick settings: small heading above the title
  wizardTitle?: string; // Quick settings: step 1 title
  wizardIntro?: string; // Quick settings: step 1 explanatory text
  faviconUrl?: string;
  permissions?: UserPermissions;
  aiGeneratorDailyLimit?: number; // Max AI icon generations per IP per day (server-enforced); logged-in admins are exempt
  aiGeneratorGlobalDailyLimit?: number; // Max AI icon images across all requests per day (server-enforced); applies to everyone, including admins
  defaultVariantSettings?: Record<string, VariantSettings>; // Stores admin-defined defaults
  sheetWidth?: number; // Print sheet size in mm (defaults to A4: 210x297)
  sheetHeight?: number;
  bleedEnabled?: boolean; // Whether the print file exports with bleed ("spadávka")
  bleedMm?: number; // Bleed size in mm, tenths precision (e.g. 1.1, 2.3)
  cutLinesEnabled?: boolean; // Whether the print file exports with vector cut paths ("ořezové cesty")
  // --- převzato z GEP/Design ---
  priceTable?: { fromSheets: number; pricePerSheet: number }[]; // orientační cena podle počtu archů
  priceCurrency?: string;
  priceNote?: string;
  preCartCheck?: boolean; // kontrola před košíkem (výchozí zapnuto)
  minPrintDpi?: number;   // hranice pro upozornění na neostrý obrázek (výchozí 150)
  aiExtraSuggestions?: boolean; // 4 návrhy AI místo 2 (výchozí zapnuto)
  czkPerUsd?: number;     // kurz pro přepočet nákladů AI
  require2faForAdmins?: boolean; // povinné dvoufázové ověření pro administrátory
  securityWebhookUrl?: string;   // bezpečnostní upozornění (jinak errorWebhookUrl)
  sharedLibrary?: SharedLibrary; // jen u tématu: sdílené složky a knihovny z hlavní adresy
}

/** Co téma sdílí z hlavní adresy (odkazem, ne kopií). Nastavuje se v Administraci → Témata. */
export interface SharedLibrary { iconCategories: string[]; backgrounds?: boolean; shapes?: boolean; templates?: boolean }

interface AssetSettings {
  customIcons?: IconType[];
  customBackgrounds?: CustomBackground[];
  customShapes?: CustomShape[];
  customTemplates?: CustomTemplate[];
}

const EMPTY_ASSETS: AssetSettings = { customIcons: [], customBackgrounds: [], customShapes: [], customTemplates: [] };

const sanitizeData = (data: any): any => {
    if (Array.isArray(data)) {
        return data.map(v => sanitizeData(v));
    } else if (data !== null && typeof data === 'object') {
        const newObj: any = {};
        Object.keys(data).forEach(key => {
            const val = data[key];
            if (val !== undefined) {
                newObj[key] = sanitizeData(val);
            }
        });
        return newObj;
    }
    return data;
};

// --- Sdílená knihovna tématu ---
// Na adrese tématu se k vlastní knihovně připojí vybrané složky ikon (a případně pozadí, tvary, šablony)
// z hlavní adresy. Sdílené položky mají příznak shared: zobrazí se, ale nikdy se neukládají do tématu –
// upravují se na hlavní adrese, takže oprava se projeví ve všech tématech.
let sharedCfg: SharedLibrary | null | undefined; // undefined = ještě nenačteno
const isThemeInstance = () => getInstanceId() !== getBaseInstanceId();

async function getSharedCfg(): Promise<SharedLibrary | null> {
    if (sharedCfg !== undefined) return sharedCfg;
    if (!db || !isThemeInstance()) return (sharedCfg = null);
    try {
        const snap = await getDoc(doc(db, SETTINGS_COLLECTION, getInstanceId()));
        sharedCfg = (snap.exists() && (snap.data() as any).sharedLibrary) || null;
    } catch { sharedCfg = null; }
    return sharedCfg;
}

/** Vlastní položky mají přednost; sdílené se stejným id se přeskočí. */
function mergeShared<T extends { id: string }>(own: T[], shared: T[]): T[] {
    const ids = new Set(own.map(o => o.id));
    return [...own, ...shared.filter(x => !ids.has(x.id)).map(x => ({ ...x, shared: true }))];
}
const stripShared = <T,>(arr: T[] | undefined): T[] | undefined =>
    Array.isArray(arr) ? arr.filter((x: any) => !x?.shared).map((x: any) => { const { shared, ...rest } = x; return rest as T; }) : arr;

async function loadSharedIcons(cfg: SharedLibrary): Promise<IconType[]> {
    if (!db || !cfg.iconCategories?.length) return [];
    const wanted = new Set(cfg.iconCategories);
    const snap = await getDocs(collection(db, SETTINGS_COLLECTION, getBaseInstanceId(), COL_ICONS));
    return snap.docs.map(d => d.data() as IconType).filter(i => wanted.has(i.categoryId || 'basic'));
}

export const settingsService = {
  // `failed` is set when the settings could not be read at all (offline, blocked reCAPTCHA/App Check): the caller
  // must not then carry on with the built-in defaults as if they were the shop's settings (wrong return URL!).
  // The icon catalog is by far the largest part (hundreds of icons incl. AI PNGs, several MB),
  // so the app loads it separately in the background instead of blocking the editor on it.
  loadCustomIcons: async (): Promise<IconType[] | null> => {
    if (!db) return null;
    try {
        const [snap, cfg] = await Promise.all([getDocs(collection(db, SETTINGS_COLLECTION, getInstanceId(), COL_ICONS)), getSharedCfg()]);
        const own = snap.docs.map(d => d.data() as IconType);
        if (!cfg) return own;
        let shared: IconType[] = [];
        try { shared = await loadSharedIcons(cfg); } catch (e) { console.error("[Settings] Shared icons failed:", e); }
        return mergeShared(own, shared);
    } catch (e) {
        console.error("[Settings] Error loading icons:", e);
        return null;
    }
  },

  loadGlobalData: async (opts?: { skipIcons?: boolean }): Promise<{ general: GeneralSettings; assets: AssetSettings; failed?: boolean }> => {
    if (!db) {
        console.warn("Firebase DB not initialized.");
        return { general: {}, assets: EMPTY_ASSETS, failed: true };
    }
    try {
        const instanceId = getInstanceId();
        const generalSnap = await getDoc(doc(db, SETTINGS_COLLECTION, instanceId));
        const [iconsSnap, bgsSnap, shapesSnap, templatesSnap] = await Promise.all([
            opts?.skipIcons ? Promise.resolve(null) : getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_ICONS)),
            getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_BGS)),
            getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_SHAPES)),
            getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_TEMPLATES))
        ]);

        const assets: AssetSettings = {
            customIcons: iconsSnap ? iconsSnap.docs.map(d => d.data() as IconType) : [],
            customBackgrounds: bgsSnap.docs.map(d => d.data() as CustomBackground),
            customShapes: shapesSnap.docs.map(d => d.data() as CustomShape),
            customTemplates: templatesSnap.docs.map(d => d.data() as CustomTemplate)
        };

        const general: GeneralSettings = generalSnap.exists() ? (generalSnap.data() as GeneralSettings) : {};
        sharedCfg = isThemeInstance() ? (general.sharedLibrary || null) : null;
        const cfg = sharedCfg;
        if (cfg) {
            // Sdílené části z hlavní adresy: složky ikon, pozadí, tvary, šablony podle nastavení tématu.
            const baseId = getBaseInstanceId();
            try {
                const [baseSnap, sIcons, sBgs, sShapes, sTemplates] = await Promise.all([
                    getDoc(doc(db, SETTINGS_COLLECTION, baseId)),
                    opts?.skipIcons ? Promise.resolve([] as IconType[]) : loadSharedIcons(cfg),
                    cfg.backgrounds ? getDocs(collection(db, SETTINGS_COLLECTION, baseId, COL_BGS)) : Promise.resolve(null),
                    cfg.shapes ? getDocs(collection(db, SETTINGS_COLLECTION, baseId, COL_SHAPES)) : Promise.resolve(null),
                    cfg.templates ? getDocs(collection(db, SETTINGS_COLLECTION, baseId, COL_TEMPLATES)) : Promise.resolve(null),
                ]);
                const baseGeneral = (baseSnap.exists() ? baseSnap.data() : {}) as GeneralSettings;
                const wanted = new Set(cfg.iconCategories || []);
                const baseCats = (baseGeneral.iconCategories || []).filter(c => wanted.has(c.id)).map(c => ({ ...c, hidden: false }));
                general.iconCategories = mergeShared(general.iconCategories || [], baseCats);
                if (sBgs) {
                    assets.customBackgrounds = mergeShared(assets.customBackgrounds || [], sBgs.docs.map(d => d.data() as CustomBackground));
                    general.bgCategories = mergeShared(general.bgCategories || [], baseGeneral.bgCategories || []);
                }
                if (sShapes) assets.customShapes = mergeShared(assets.customShapes || [], sShapes.docs.map(d => d.data() as CustomShape));
                if (sTemplates) assets.customTemplates = mergeShared(assets.customTemplates || [], sTemplates.docs.map(d => d.data() as CustomTemplate));
                if (sIcons.length) assets.customIcons = mergeShared(assets.customIcons || [], sIcons);
            } catch (e) { console.error("[Settings] Shared library failed:", e); }
        }

        return { general, assets };
    } catch (e: any) {
        console.error("[Settings] Error loading data:", e);
        return { general: {}, assets: EMPTY_ASSETS, failed: true };
    }
  },

  saveGeneralSettings: async (settings: Partial<GeneralSettings>) => {
    if (!db) return;
    try {
        const s: any = { ...settings };
        if ('iconCategories' in s) s.iconCategories = stripShared(s.iconCategories);
        if ('bgCategories' in s) s.bgCategories = stripShared(s.bgCategories);
        delete s.sharedLibrary; // mění jen server (Administrace → Témata)
        const cleanData = sanitizeData(s);
        await setDoc(doc(db, SETTINGS_COLLECTION, getInstanceId()), cleanData, { merge: true });
    } catch (e) {
        console.error("Failed to save settings:", e);
        throw e;
    }
  },

  removeVariantDefault: async (variantId: string) => {
    if (!db) return;
    try {
        await setDoc(doc(db, SETTINGS_COLLECTION, getInstanceId()), {
            defaultVariantSettings: { [variantId]: deleteField() }
        }, { merge: true });
    } catch (e) {
        console.error("Failed to remove variant default:", e);
        throw e;
    }
  },

  saveCustomIcons: async (icons: IconType[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        const iconsToStore = icons.filter(i => !(i as any).shared && (i.isCustom || i.categoryId));
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_ICONS));
        const currentIds = new Set(iconsToStore.map(i => i.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        iconsToStore.forEach(icon => {
            const cleanIcon = sanitizeData({
                id: icon.id,
                name: icon.name,
                dataType: icon.dataType,
                data: icon.data,
                isCustom: !!icon.isCustom,
                categoryId: icon.categoryId || 'basic'
            });
            const iconRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_ICONS, icon.id);
            batch.set(iconRef, cleanIcon);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save icons to collection:", e);
        throw e;
    }
  },

  saveCustomBackgrounds: async (bgs: CustomBackground[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        bgs = bgs.filter((x: any) => !x.shared);
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_BGS));
        const currentIds = new Set(bgs.map(b => b.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        bgs.forEach(bg => {
            const cleanBg = sanitizeData(bg);
            const bgRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_BGS, bg.id);
            batch.set(bgRef, cleanBg);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save backgrounds to collection:", e);
        throw e;
    }
  },

  saveCustomTemplates: async (templates: CustomTemplate[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        templates = templates.filter((x: any) => !x.shared);
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_TEMPLATES));
        const currentIds = new Set(templates.map(t => t.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        templates.forEach(t => {
            const cleanTemplate = sanitizeData(t);
            const templateRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_TEMPLATES, t.id);
            batch.set(templateRef, cleanTemplate);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save templates to collection:", e);
        throw e;
    }
  },

  saveCustomShapes: async (shapes: CustomShape[]) => {
    if (!db) return;
    try {
        const instanceId = getInstanceId();
        shapes = shapes.filter((x: any) => !x.shared);
        const batch = writeBatch(db);
        const existingSnap = await getDocs(collection(db, SETTINGS_COLLECTION, instanceId, COL_SHAPES));
        const currentIds = new Set(shapes.map(s => s.id));

        existingSnap.docs.forEach(docSnap => {
            if (!currentIds.has(docSnap.id)) {
                batch.delete(docSnap.ref);
            }
        });

        shapes.forEach(shape => {
            const cleanShape = sanitizeData(shape);
            const shapeRef = doc(db, SETTINGS_COLLECTION, instanceId, COL_SHAPES, shape.id);
            batch.set(shapeRef, cleanShape);
        });

        await batch.commit();
    } catch (e) {
        console.error("Failed to save shapes to collection:", e);
        throw e;
    }
  }
};
